import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import wallpaper from '@/assets/onboarding-home-wallpaper.png';
import {
  IOS_APP_LABEL,
  IOS_APP_LABEL_SPACE_WIDTH,
  layoutIosAppLabel,
  type IosAppLabel,
} from '@/lib/ios-home-screen/ios-app-label-layout';
import { MockupFramedByCanvasContext } from '@/pages/onboarding/mockup/mockup-canvas-context';

export interface HomeScreenSceneProps {
  appName: string | null;
  appIconUrl: string | null;
  /**
   * The app's own screen, opened from (or closed back into) its icon inside
   * this phone, the way iOS opens an app: grows from the icon to the whole
   * screen while `opened`. Used below lg, where the page transition cannot
   * morph the icon (see AppOpenScene's `enableIconMorph`).
   */
  openApp?: { opened: boolean; screen: ReactNode };
}

const PLACEHOLDER_APP_NAME = 'Seu App';

// Position of the one empty grid slot in the wallpaper (the "Belletonn"
// placeholder app, row 3 col 1), expressed as % of the wallpaper image so
// it stays aligned regardless of the rendered container size. Measured by
// sampling the exported PNG's own pixels for the icon's blue fill
// (x:[63,188], y:[594,720] in the 826×1789 export) rather than from the
// Figma layout tree's declared paddings/gaps — those summed to a ~20px
// offset from where the icon actually sits, which showed up as a sliver of
// the original blue icon peeking out under our own rendered icon.
const ICON_SLOT = { left: 7.6271, top: 33.2029, width: 15.1332 };

// The label sits in a wider box than the icon (184 vs 125.972 design
// units), centered under it — same as every other icon label in this
// Figma frame. Erasing "Belletonn" had to clear that full wider footprint
// (its label overhangs its icon on both sides), so the label here is kept
// just as wide: it re-covers the erased area almost exactly instead of
// leaving a sliver of cloned-background patch exposed beside a
// narrower label.
const LABEL_WIDTH = 22.301; // 184 / 825.118 * 100
const LABEL_LEFT = ICON_SLOT.left + ICON_SLOT.width / 2 - LABEL_WIDTH / 2;
const ICON_WIDTH_IN_LABEL = (125.972 / 184) * 100;

// However close ICON_SLOT's measurement gets, a sub-pixel gap between our
// rendered icon and the wallpaper's own baked-in placeholder icon (still
// sitting underneath, unerased — see the comment above) is basically
// unavoidable at this scale. Invisible normally, but this scene also
// renders zoomed in (ZoomedIPhoneMockup, up to ~1.55x) on the app-name and
// app-logo steps, which magnifies any such gap into a visible ring of the
// placeholder's blue peeking out around our icon — worse the higher the
// contrast with the lead's own icon (a dark icon against the wallpaper's
// red/orange backdrop shows it far more than a lighter one did in testing).
// Scaling our icon slightly past the measured bounds, from its own center,
// trades a visually-imperceptible size bump for guaranteed full coverage
// at any zoom level, without touching the positioning math above.
const ICON_OVERSCAN_SCALE = 1.08;

// The wallpaper is a 393pt-wide iPhone frame and the scene fills it.
const FRAME_WIDTH_PT = 393;

// The icon as drawn (ICON_SLOT grown by ICON_OVERSCAN_SCALE around its
// center), in % of the screen: its height uses the wallpaper's own aspect
// (826×1789).
const SCREEN_ASPECT = 1789 / 826;
const ICON_RECT = (() => {
  const width = ICON_SLOT.width * ICON_OVERSCAN_SCALE;
  const height = width / SCREEN_ASPECT;
  const centerX = ICON_SLOT.left + ICON_SLOT.width / 2;
  const centerY = ICON_SLOT.top + ICON_SLOT.width / SCREEN_ASPECT / 2;
  return {
    left: centerX - width / 2,
    top: centerY - height / 2,
    width,
    height,
    centerX,
    centerY,
  };
})();

/** How long the app takes to open from (or close into) its icon. */
export const APP_OPEN_DURATION_MS = 500;
const APP_OPEN_TRANSITION = `${APP_OPEN_DURATION_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`;
const ICON_FADE_IN_DELAY_MS = 250;

/**
 * The app growing out of its icon: its screen scaled down around the icon's
 * center and clipped to the icon's rounded square while closed, the icon's
 * own art on top fading out as it opens.
 */
function OpeningApp({
  opened,
  screen,
  iconCornerRadius,
  icon,
}: {
  opened: boolean;
  screen: ReactNode;
  iconCornerRadius: number;
  icon: ReactNode;
}) {
  const scale = ICON_RECT.width / 100;
  const { left, top, width, height } = ICON_RECT;
  const transition = `transform ${APP_OPEN_TRANSITION}, clip-path ${APP_OPEN_TRANSITION}, opacity ${APP_OPEN_TRANSITION}`;
  return (
    <div
      aria-hidden={!opened}
      className="pointer-events-none absolute inset-0 motion-reduce:!transition-none"
      style={{
        clipPath: opened
          ? 'inset(0 round 0)'
          : `inset(${top}% ${100 - left - width}% ${100 - top - height}% ${left}% round ${iconCornerRadius}px)`,
        transition,
        pointerEvents: opened ? 'auto' : 'none',
      }}
    >
      <div
        className="h-full w-full motion-reduce:!transition-none"
        style={{
          transformOrigin: `${ICON_RECT.centerX}% ${ICON_RECT.centerY}%`,
          transform: opened ? 'none' : `scale(${scale})`,
          transition,
        }}
      >
        {screen}
      </div>
      <div
        className="absolute motion-reduce:!transition-none"
        style={{
          left: `${left}%`,
          top: `${top}%`,
          width: `${width}%`,
          height: `${height}%`,
          opacity: opened ? 0 : 1,
          // Fades out as the app starts opening; back in only once the
          // closing app is about icon-sized, as iOS does.
          transition: `opacity 250ms ease-out ${opened ? 0 : ICON_FADE_IN_DELAY_MS}ms`,
        }}
      >
        {icon}
      </div>
    </div>
  );
}

// Icon bottom → top of the label's line box, so the cap height starts 8.6pt
// under the icon like the wallpaper's other labels ("Mail", "Notes"…).
const LABEL_GAP = 5.6;

/**
 * `url` once its image is decoded, the previous one until then: the logo
 * step swaps in a freshly framed logo right as the page appears, and a bare
 * new src would show the icon's empty white tile for a few frames (caught by
 * the page transition's snapshot, too).
 */
function useDecodedImageUrl(url: string | null): string | null {
  const [shown, setShown] = useState(url);
  useEffect(() => {
    if (url === shown) return;
    if (!url) {
      setShown(null);
      return;
    }
    let cancelled = false;
    const image = new Image();
    image.src = url;
    image
      .decode()
      .catch(() => undefined)
      .then(() => {
        if (!cancelled) setShown(url);
      });
    return () => {
      cancelled = true;
    };
  }, [url, shown]);
  return shown;
}

/** Scene pixels per point (0 until measured). */
function useScenePoint() {
  const ref = useRef<HTMLDivElement>(null);
  const [point, setPoint] = useState(0);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    // Layout width: the mockup's zoom transforms scale the label with it.
    const measure = () => setPoint(element.offsetWidth / FRAME_WIDTH_PT);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, point] as const;
}

/**
 * The app name as the iOS 26 home screen draws it (layoutIosAppLabel: SF Pro
 * 12pt, letter spacing tightened, spaces narrowed or "…", decided with SF
 * Pro's own metrics whatever the browser's font). Set at a real 12px and
 * scaled down to the mockup: Apple browsers space SF by its font size, so
 * text set at the mockup's ~9px would come out wider than the phone's 12pt.
 * CSS letter spacing also follows the last glyph, which iOS does not: the
 * padding gives it back so the name stays centered.
 */
function appLabelStyle(
  { tracking, narrowSpaces }: IosAppLabel,
  point: number
): CSSProperties {
  return {
    fontSize: IOS_APP_LABEL.fontSize,
    lineHeight: 1.2,
    letterSpacing: -tracking,
    paddingRight: tracking,
    wordSpacing: narrowSpaces
      ? IOS_APP_LABEL.narrowSpace - IOS_APP_LABEL_SPACE_WIDTH
      : undefined,
    transformOrigin: '0 0',
    transform: `scale(${point}) translateX(-50%)`,
    visibility: point > 0 ? undefined : 'hidden',
  };
}

// Unlocked home-screen scene: the wallpaper is a pixel-exact export of
// Korza's own "Home Screen - iPhone" Figma node (Templates Korza, node
// 1338:12363) — status bar, icon grid, search field and dock all baked in
// at the same proportions as the design, so IPhoneMockup renders this scene
// with `chromeBaked` (no separate status bar overlay/home indicator, since
// the wallpaper already draws its own). The design ships one app icon as a
// placeholder ("Belletonn") in that slot; only its label text was erased
// from the export (cloned from the empty background beside it, not a solid
// fill, so the gradient stays continuous) — its icon artwork was left
// untouched, since our own icon renders at that exact position/size and
// fully occludes it, no erasure needed there. The lead's app icon is
// placed into that same slot, like any other app on the grid.
export function HomeScreenScene({
  appName,
  appIconUrl,
  openApp,
}: HomeScreenSceneProps) {
  const displayName = appName || PLACEHOLDER_APP_NAME;
  const label = layoutIosAppLabel(displayName);
  const [sceneRef, point] = useScenePoint();
  const iconUrl = useDecodedImageUrl(appIconUrl);
  // In the mobile Studio the app opens inside the phone (openApp) instead.
  const framedByCanvas = useContext(MockupFramedByCanvasContext);

  const iconArt = iconUrl ? (
    <img
      src={iconUrl}
      alt={displayName}
      decoding="sync"
      className="h-full w-full object-cover"
    />
  ) : (
    <span className="flex h-full w-full items-center justify-center text-center text-[9px] font-medium text-muted-foreground">
      {PLACEHOLDER_APP_NAME}
    </span>
  );

  return (
    <div
      ref={sceneRef}
      className="relative h-full w-full overflow-hidden bg-cover bg-center"
      style={{ backgroundImage: `url(${wallpaper})` }}
    >
      <div
        className="absolute flex flex-col items-center"
        style={{
          left: `${LABEL_LEFT}%`,
          top: `${ICON_SLOT.top}%`,
          width: `${LABEL_WIDTH}%`,
        }}
      >
        <div
          className="aspect-square overflow-hidden rounded-[22%] bg-white"
          style={{
            width: `${ICON_WIDTH_IN_LABEL}%`,
            transform: `scale(${ICON_OVERSCAN_SCALE})`,
            // Shared with AppOpenScene's root (same name) so navigating into
            // the tab-bar/notification steps morphs this icon into the full
            // screen via the browser's View Transition API, like the real
            // iOS "app opening" zoom — see onSubmit's `viewTransition: true`
            // on the onboarding pages' navigate() calls.
            viewTransitionName: framedByCanvas
              ? undefined
              : 'onboarding-app-icon',
          }}
        >
          {iconArt}
        </div>
        <div
          className="relative w-full"
          style={{ marginTop: LABEL_GAP * point }}
        >
          <span
            className="absolute left-1/2 top-0 whitespace-pre font-normal text-white drop-shadow"
            style={appLabelStyle(label, point)}
          >
            {label.text}
          </span>
        </div>
      </div>
      {openApp && (
        <OpeningApp
          opened={openApp.opened}
          screen={openApp.screen}
          // rounded-[22%] of the icon, which is ICON_RECT.width% of the scene.
          iconCornerRadius={
            0.22 * (ICON_RECT.width / 100) * FRAME_WIDTH_PT * point
          }
          icon={
            <div className="h-full w-full overflow-hidden rounded-[22%] bg-white">
              {iconArt}
            </div>
          }
        />
      )}
    </div>
  );
}
